import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { v4 } from 'uuid';
import { DialogService } from '../../../dialog/dialog.service';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { AccountQuotas } from '../../../features-data/sub-models/account-quotas.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import {
  ITrackingAction,
  ITrackingObject,
} from '../../../tracking/tracking.data';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-add-attachments',
  templateUrl: 'add-attachments.component.html',
  styleUrls: ['add-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddAttachmentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() enableMultipleFiles: boolean = true;
  @Input() icon: IIcon;
  @Input() disabled: boolean;
  @Input() checkQuotaAndFeature: boolean;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() featureToggles: Toggles;

  @Output()
  onAttachFiles: EventEmitter<FileList> = new EventEmitter<FileList>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  showFeatureHighlight: boolean;
  errorMessage: string;
  readOnlyAccess: boolean;
  attachmentSelectId: string;
  acceptableFileTypes: string[];

  constructor(
    private _featuresDataStore: FeaturesDataStore,
    private _dialogService: DialogService,
    private _translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.featureToggles && this.featureToggles) {
      this.readOnlyAccess = this.featureToggles.isReadOnlyAccess(
        Toggles.KEYS.ATTACHMENT
      );
    }
  }

  ngOnInit() {
    this.attachmentSelectId = v4();
    this.acceptableFileTypes = ['image/png', 'image/jpeg'];

    this._featuresDataStore
      .hasFeatureHighlightCanvas(Toggles.KEYS.ATTACHMENT)
      .pipe(takeUntil(this._destroyed))
      .subscribe(
        (showFeatureHighlight: boolean) =>
          (this.showFeatureHighlight =
            showFeatureHighlight && this.checkQuotaAndFeature)
      );
  }

  onAddFiles(evt: any) {
    const inputElement = evt.srcElement || evt.target;

    this.errorMessage = null;

    if (!this.disabled) {
      this._featuresDataStore
        .getAccountQuotas()
        .pipe(takeUntil(this._destroyed))
        .subscribe((accountQuotas: AccountQuotas) => {
          if (this.checkQuotaAndFeature && !accountQuotas.storage.canCreate) {
            evt.preventDefault();

            this._dialogService.openGrowthDialog(
              BannerCanvasTypesEnum.quota_reached,
              AccountQuotas.KEYS.STORAGE,
              this.growthCanvasCallback
            );
          } else {
            for (const file of inputElement.files) {
              if (!this.acceptableFileTypes.includes(file.type)) {
                this.errorMessage = this._translateService.instant(
                  'ERROR.UNSUPPORTED_FILE_TYPE'
                );
                inputElement.value = null;
                return;
              }
            }

            this.onAttachFiles.emit(inputElement.files);
          }

          inputElement.value = null;
        });
    }
  }

  onUploadAttempt(evt: Event) {
    const featureName = Toggles.KEYS.ATTACHMENT;

    if (
      this.checkQuotaAndFeature &&
      this.featureToggles.isNoAccess(featureName) &&
      !this.disabled
    ) {
      evt.preventDefault();

      this._dialogService.openGrowthDialog(
        BannerCanvasTypesEnum.feature_lock,
        featureName,
        this.growthCanvasCallback
      );
    } else if (this.disabled) {
      evt.preventDefault();
    }
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      Toggles.KEYS.ATTACHMENT,
      this.growthCanvasCallback
    );
  }

  growthCanvasCallback = () => this.onGrowthCta.emit();

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
